import DOMPurify from 'dompurify';
import React, { Fragment, useState } from 'react';
import { MDFContentPane, MDFModalDialog } from '../../../index';
import { Currency } from './Currency';
import { Date } from './Date';
import { Number } from './Number';
import { OneOf } from './OneOf';
import { SingleOption } from './SingleOption';
import { SomeOf } from './SomeOf';
import { String } from './String';
import { Field, FieldChangeEvent } from './types';
import { YesNo } from './YesNo';

const FIELD_COMPONENTS = {
  OneOf: OneOf,
  SomeOf: SomeOf,
  YesNo: YesNo,
  String: String,
  Number: Number,
  Date: Date,
  Currency: Currency,
  SingleOption: SingleOption
};

const FieldWrapper: React.FC<{
  field: Field;
  onChange: (field: Field, event: FieldChangeEvent) => void;
}> = ({ field, onChange }) => {
  const [showDoubleDare, setShowDoubleDare] = useState(false);
  const [pendingValue, setPendingValue] = useState(null);

  const Component = FIELD_COMPONENTS[field.type];

  if (!Component) {
    return null;
  }

  const shouldTriggerDoubleDare = (value: any): boolean => {
    if (!field.doubleDare) {
      return false;
    }

    const safeValue = field.doubleDare.safeData.value;

    if (field.type === 'SomeOf') {
      return !value?.includes(safeValue);
    }

    return value !== safeValue;
  };

  const handleChange = (value: any) => {
    if (shouldTriggerDoubleDare(value)) {
      setPendingValue(value);
      setShowDoubleDare(true);
      return;
    }

    onChange(field, { fieldId: field.id, value: value, fieldType: field.type });
  };

  const handleDoubleDareYes = () => {
    setShowDoubleDare(false);
    onChange(field, { fieldId: field.id, value: pendingValue, fieldType: field.type });
    setPendingValue(null);
  };

  const handleDoubleDareNo = () => {
    setShowDoubleDare(false);
    setPendingValue(null);
  };

  return (
    <Fragment>
      <Component {...field} onChange={handleChange} />
      {field.doubleDare && (
        <MDFModalDialog
          show={showDoubleDare}
          modalType={'Alert'}
          size={'md'}
          backdrop={true}
          closeable={false}
          title={field.doubleDare.title}
          yesButtonText={field.doubleDare.yesLabel}
          noButtonText={field.doubleDare.noLabel}
          onYes={handleDoubleDareYes}
          onNo={handleDoubleDareNo}
        >
          <MDFContentPane html={DOMPurify.sanitize(field.doubleDare.content || '')} />
        </MDFModalDialog>
      )}
    </Fragment>
  );
};

export function renderField(field: Field, onChange: (field: Field, event: FieldChangeEvent) => void): React.ReactNode {
  return <FieldWrapper key={field.id} field={field} onChange={onChange} />;
}
